import {QueryLazyOptions} from '@apollo/react-hooks';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import * as React from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Popup} from 'semantic-ui-react';

import * as Generated from '../../generated/graphql';
import {Banner, BannerFlexWrapper, BannerTextWrapper} from '../Banner';
import {FilterInfo, FilterRow, FilterValueTypes} from './MetadataFilterRow';
import {
  compareFiltersEqual,
  filterInfoToMongoFilter,
  filtersAreValid,
} from './utils';

interface MetadataFilterPopupProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialFilterState: FilterInfo[];
  currentFilterRows: FilterInfo[]; // Current FilterRows
  setCurrentFilterRows: (filters: any) => void;
  savedFilterRows: FilterInfo[];
  setSavedFilterRows: (filters: FilterInfo[]) => void;
  getMetadataSearchArtifacts: (
    options?:
      | QueryLazyOptions<
          Generated.Exact<{
            projectName: string;
            entityName: string;
            filters: any;
          }>
        >
      | undefined
  ) => void;
  artifactMetadataSearchVariables: Generated.ArtifactProjectMetadataSearchQueryVariables;
  setErrorFetchingMetadataQuery: (error: boolean) => void;
}

export const MetadataFilterPopup = (props: MetadataFilterPopupProps) => {
  const {
    open,
    setOpen,
    initialFilterState,
    currentFilterRows,
    setCurrentFilterRows,
    savedFilterRows,
    setSavedFilterRows,
    getMetadataSearchArtifacts,
    artifactMetadataSearchVariables,
    setErrorFetchingMetadataQuery,
  } = props;

  const addFilter =
    (id: number) => (type: FilterValueTypes, key?: string, value?: string) =>
      setCurrentFilterRows(
        currentFilterRows.map(f =>
          f.id === id
            ? {
                ...f,
                value,
                key,
                type,
              }
            : f
        )
      );

  const deleteFilter = (id: number) => () => {
    if (currentFilterRows.length === 1) {
      // this is the last filter to be deleted, default to a blank filter
      setCurrentFilterRows(initialFilterState);
    } else {
      setCurrentFilterRows(currentFilterRows.filter(f => f.id !== id));
    }
  };

  return (
    <Popup
      basic
      className="artifacts-filter-popup"
      style={{
        maxWidth: 'none',
        borderRadius: '8px',
        boxShadow: '0px 12px 24px 0px',
      }}
      on="click"
      position="bottom left"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      popperModifiers={{
        preventOverflow: {enabled: false},
        flip: {enabled: false},
      }}
      trigger={
        <div
          className={`min-h-32 min-w-32 mr-8 flex flex-initial rounded px-5 py-4 ${
            !compareFiltersEqual(initialFilterState, savedFilterRows)
              ? 'bg-teal-300/[.47]'
              : 'bg-moon-200/[.47]'
          }`}>
          <Icon
            name="filter-alt"
            className={`min-h-full min-w-full cursor-pointer align-middle ${
              !compareFiltersEqual(initialFilterState, savedFilterRows)
                ? 'text-teal-600'
                : 'text-moon-700'
            }`}
          />
        </div>
      }
      content={
        <Tailwind style={{width: '100%', height: '100%'}}>
          <div className="flex flex-col gap-12">
            <Banner variant="info">
              <BannerFlexWrapper>
                <Icon name="lightbulb-info" />
                <BannerTextWrapper>
                  Filter only on artifact version metadata. Only top level keys
                  are indexed.
                </BannerTextWrapper>
              </BannerFlexWrapper>
            </Banner>
            {currentFilterRows.map(item => {
              return (
                <FilterRow
                  key={item.id}
                  addFilter={addFilter(item.id)}
                  deleteFilter={deleteFilter(item.id)}
                  filter={item}
                />
              );
            })}
            <div className="flex justify-between">
              <div
                onClick={_ => {
                  setCurrentFilterRows((filters: FilterInfo[]) => [
                    ...filters,
                    {id: Math.random(), type: FilterValueTypes.String},
                  ]);
                }}
                className="flex cursor-pointer">
                <Icon name="add-new" className="pr-4 text-moon-500" />
                <div className="font-source-sans-pro text-sm font-semibold leading-5 text-moon-500">
                  Add metadata filter
                </div>
              </div>
              <div>
                <Button
                  onClick={async () => {
                    setErrorFetchingMetadataQuery(false);
                    if (
                      filtersAreValid(currentFilterRows, initialFilterState)
                    ) {
                      try {
                        const mongoFilterString = filterInfoToMongoFilter(
                          currentFilterRows,
                          initialFilterState
                        );
                        if (mongoFilterString !== '') {
                          await getMetadataSearchArtifacts({
                            variables: {
                              ...artifactMetadataSearchVariables,
                              filters: mongoFilterString,
                            },
                          });
                        }
                        setSavedFilterRows(currentFilterRows);
                      } catch (e) {
                        setErrorFetchingMetadataQuery(true);
                        console.error(
                          'Error fetching artifact metadata search query ',
                          e
                        );
                      }
                    } else {
                      setErrorFetchingMetadataQuery(true);
                      console.error(
                        'Error formatting artifact metadata search query to mongo ',
                        currentFilterRows
                      );
                    }
                    setOpen(false);
                  }}
                  icon="running-repeat"
                  size="small"
                  variant="primary"
                  disabled={
                    compareFiltersEqual(currentFilterRows, savedFilterRows) ||
                    !filtersAreValid(currentFilterRows, initialFilterState)
                  }>
                  Update results
                </Button>
              </div>
            </div>
          </div>
        </Tailwind>
      }
    />
  );
};
